import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import styled from "styled-components"
import { getSafeSyntaxLanguage } from "../../utils/syntaxHighlighterUtil";


const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
`;

const ModalWrapper = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
`;

const ModalContent = styled.div`
  background-color: rgba(255, 255, 255, 1);
  backdrop-filter: blur(10px);
  border-radius: 1rem;
  padding: 2rem;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  /* Scrollbar Styles */
  scrollbar-width: thin;
  scrollbar-color: rgba(155, 155, 155, 0.5) transparent;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    margin: 1rem;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.5);
    border-radius: 20px;
    border: 3px solid rgba(255, 255, 255, 0.75);
  }
  /* Ensure content doesn't overlap with scrollbar */
  padding-right: calc(2rem + 8px);
`;

const CloseButtonContainer = styled.div`
  position: absolute;
  top: 0.5rem;
  right: 1rem;
  z-index: 60;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.8);
  border: none;
  border-radius: 50%;
  font-size: 1.5rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  color: #666;
  &:hover {
    color: #000;
    background: rgba(255, 255, 255, 1);
  }
`;

const MarkdownContent = styled.div`
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
    "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
    sans-serif;
  line-height: 1.6;
  color: #333;
  h1 {
    font-size: 2em;
    margin-bottom: 0.5em;
    border-bottom: 1px solid #eaecef;
    padding-bottom: 0.3em;
  }
  h2 {
    font-size: 1.8em;
    margin-top: 0.5em;
    padding-bottom: 0.1em;
  }
  h3 {
    font-size: 1.5em;
    margin-top: 0.5em;
    padding-bottom: 0.1em;
  }
  h4 {
    font-size: 1.2em;
    margin-top: 0.5em;
  }
  h5 {
    font-size: 1em;
    margin-top: 0.5em;
  }
  a {
    color: #0366d6;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
  code {
    background-color: rgba(27, 31, 35, 0.05);
    border-radius: 3px;
    font-size: 85%;
    margin: 0;
    padding: 0.2em 0.4em;
  }
  pre {
    background-color: #f6f8fa;
    border-radius: 3px;
    font-size: 85%;
    line-height: 1.45;
    overflow: auto;
    padding: 16px;
  }
  ul,
  ol {
    padding-left: 2.5em;
    margin-bottom: 1em;
  }
  li {
    margin-bottom: 0.5em;
    position: relative;
  }
  ul li::before {
    content: "-";
    position: absolute;
    left: -1.2em;
  }
  ol {
    counter-reset: item;
  }
  ol li {
    counter-increment: item;
  }
  ol li::before {
    content: counter(item) ".";
    position: absolute;
    left: -1.5em;
    width: 1em;
    text-align: right;
  }
  ul ul,
  ol ol,
  ul ol,
  ol ul {
    margin-top: 0.5em;
    margin-bottom: 0;
  }
`;

const HelpModal = ({ fileName, isOpen, onClose }) => {
  const [markdownContent, setMarkdownContent] = useState("");
  const modalRef = useRef();

  const handleClickOutside = useCallback(
    (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    },
    [onClose]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );

  const handlePopState = useCallback(() => {
    if (isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    const fetchMarkdownContent = async () => {
      if (isOpen) {
        try {
          const response = await fetch(`/help/${fileName}.md`);
          if (!response.ok) {
            throw new Error("Failed to fetch markdown content");
          }
          const content = await response.text();
          setMarkdownContent(content);
        } catch (error) {
          console.error("Error loading markdown file:", error);
          setMarkdownContent("Error loading content. Please try again later.");
        }
      }
    };

    fetchMarkdownContent();

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
      window.addEventListener("popstate", handlePopState);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [fileName, isOpen, handleClickOutside, handleKeyDown, handlePopState]);

  if (!isOpen) return null;

  return (
    <ModalOverlay>
      <ModalWrapper ref={modalRef}>
        <CloseButtonContainer>
          <CloseButton title="Close [ESC]" onClick={onClose}>
            &times;
          </CloseButton>
        </CloseButtonContainer>
        <ModalContent>
          <MarkdownContent>
            <ReactMarkdown
              rehypePlugins={[rehypeRaw]}
              remarkPlugins={[remarkGfm]}
              components={{
                code: ({ node, inline, className, children, ...props }) => {
                  const match = /language-(\w+)/.exec(className || "");
                  return !inline && match ? (
                    <SyntaxHighlighter
                      style={vscDarkPlus}
                      language={getSafeSyntaxLanguage(match[1])}
                      PreTag="div"
                      {...props}
                    >
                      {String(children).replace(/\n$/, "")}
                    </SyntaxHighlighter>
                  ) : (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
                a: ({ node, ...props }) => (
                  <a target="_blank" rel="noopener noreferrer" {...props} />
                ),
              }}
            >
              {markdownContent}
            </ReactMarkdown>
          </MarkdownContent>
        </ModalContent>
      </ModalWrapper>
    </ModalOverlay>
  );
};

export default HelpModal;
